/**
 * 通用资源 genericResourceManage · 全量 assetType 注册表。
 * assetType 字符串与后端 MusicTypeEnum.name / listAllWallpaperType 返回值一致。
 */

import { isZipOnlyType } from '../genericResourceUploadRules'
import {
  hasZipPackageSpec,
  resolveZipPackageSpec,
  summarizeZipPackageSpec,
} from './genericResourceZipPackageSpec'

export const GENERIC_RESOURCE_BASIC_FORM_MODE = {
  ZIP_HINT: 'zip_hint',
  SCHEMA: 'schema',
  SOUNDS: 'sounds',
  ZIP_CUSTOM: 'zip_custom',
  FILE_GENERIC: 'file_generic',
  WORKOUT: 'workout',
  AI_ASSISTANT: 'ai_assistant',
}

export const GENERIC_RESOURCE_GOVERNANCE_SECTIONS = {
  unlock: { component: 'GenericResourceUnlockSection', title: '解锁组' },
  classify: { component: 'GenericResourceClassifySection', title: '关联分类' },
  tag: { component: 'GenericResourceTagSection', title: '关联 Tag' },
  filter: { component: 'GenericResourceFilterSection', title: '过滤条件' },
  other: { component: 'GenericResourceOtherSection', title: '其他' },
}

export const GENERIC_RESOURCE_GOVERNANCE_PROFILES = {
  // Tag 在分类前：动作资源 Tag 必填，必填区块靠前；分类仍为选填
  link_default: ['unlock', 'tag', 'classify', 'filter', 'other'],
  link_minimal: ['unlock', 'filter', 'other'],
  basic_only: [],
}

/** @type {Record<string, object>} */
export const GENERIC_RESOURCE_TYPE_REGISTRY = {
  音乐: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.FILE_GENERIC,
    basicForm: 'FileGenericBasicForm',
    zipOnly: false,
  },
  'sounds(白噪音)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SOUNDS,
    basicForm: 'SoundsBasicForm',
    zipOnly: false,
  },
  'music(白噪音)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SOUNDS,
    basicForm: 'SoundsBasicForm',
    zipOnly: false,
  },
  'sleep story(白噪音)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SOUNDS,
    basicForm: 'SoundsBasicForm',
    zipOnly: false,
  },
  'meditation(白噪音)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SOUNDS,
    basicForm: 'SoundsBasicForm',
    zipOnly: false,
  },
  'ASMR(白噪音)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SOUNDS,
    basicForm: 'SoundsBasicForm',
    zipOnly: false,
  },
  Filter: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_CUSTOM,
    basicForm: 'ZipCustomBasicForm',
    zipOnly: true,
  },
  Template: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_CUSTOM,
    basicForm: 'ZipCustomBasicForm',
    zipOnly: true,
  },
  Flow: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_CUSTOM,
    basicForm: 'ZipCustomBasicForm',
    zipOnly: true,
  },
  Clothing: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_CUSTOM,
    basicForm: 'ZipCustomBasicForm',
    zipOnly: true,
  },
  'coupon(优惠券)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SCHEMA,
    basicForm: 'SchemaBasicForm',
    zipOnly: false,
    schemaVariant: 'coupon',
  },
  'web(网页)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SCHEMA,
    basicForm: 'SchemaBasicForm',
    zipOnly: false,
    schemaVariant: 'web',
  },
  'sounds_live(白噪音)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.SOUNDS,
    basicForm: 'SoundsBasicForm',
    zipOnly: false,
  },
  'recipe(食谱)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_HINT,
    basicForm: 'ZipHintBasicForm',
    zipOnly: true,
  },
  'puzzle(拼图)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_HINT,
    basicForm: 'ZipHintBasicForm',
    zipOnly: true,
  },
  'level(关卡)': {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_HINT,
    basicForm: 'ZipHintBasicForm',
    zipOnly: true,
  },
  recipe_v2: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_HINT,
    basicForm: 'ZipHintBasicForm',
    zipOnly: true,
  },
  workout_action: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT,
    basicForm: 'WorkoutActionBasicForm',
    zipOnly: true,
  },
  workout_music: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT,
    basicForm: 'WorkoutMusicBasicForm',
    zipOnly: true,
  },
  workout_voice: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT,
    basicForm: 'WorkoutVoiceBasicForm',
    zipOnly: true,
  },
  workout_collection: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT,
    basicForm: 'WorkoutCollectionBasicForm',
    zipOnly: true,
  },
  ai_assistant: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.AI_ASSISTANT,
    basicForm: 'AiAssistantBasicForm',
    zipOnly: false,
  },
  // zip-only：与后端 GenericResourceSubTypeEnum.Badge / Article 的 assetType 一致
  badge: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT,
    basicForm: 'BadgeBasicForm',
    zipOnly: true,
  },
  article: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT,
    basicForm: 'ArticleBasicForm',
    zipOnly: true,
  },
  stickers: {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.WORKOUT,
    basicForm: 'StickerBasicForm',
    zipOnly: true,
  },
}

export function isSoundsAssetType(assetType) {
  return String(assetType || '').includes('白噪音')
}

/**
 * 解析 assetType → registry entry。
 * 白噪音子类型走统一 Sounds 配置；未命中但为 zip-only 时 fallback zip_hint。
 */
export function resolveGenericResourceTypeEntry(assetType) {
  const type = String(assetType || '').trim()
  if (!type) {
    return null
  }
  if (GENERIC_RESOURCE_TYPE_REGISTRY[type]) {
    return { ...GENERIC_RESOURCE_TYPE_REGISTRY[type], assetType: type }
  }
  if (isSoundsAssetType(type)) {
    return {
      ...GENERIC_RESOURCE_TYPE_REGISTRY['sounds(白噪音)'],
      assetType: type,
    }
  }
  if (isZipOnlyType(type)) {
    return {
      mode: GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_HINT,
      basicForm: 'ZipHintBasicForm',
      zipOnly: true,
      assetType: type,
    }
  }
  return {
    mode: GENERIC_RESOURCE_BASIC_FORM_MODE.FILE_GENERIC,
    basicForm: 'FileGenericBasicForm',
    zipOnly: false,
    assetType: type,
  }
}

export function resolveBasicFormComponent(assetType) {
  const entry = resolveGenericResourceTypeEntry(assetType)
  return entry ? entry.basicForm : null
}

export function isRegisteredGenericResourceType(assetType) {
  return !!resolveGenericResourceTypeEntry(assetType)
}

export function resolveFormLayout(assetType, governanceProfile = 'link_default') {
  const entry = resolveGenericResourceTypeEntry(assetType) || {}
  const sectionIds =
    GENERIC_RESOURCE_GOVERNANCE_PROFILES[governanceProfile] ||
    GENERIC_RESOURCE_GOVERNANCE_PROFILES.link_default
  const sections = sectionIds
    .map((id) => {
      const meta = GENERIC_RESOURCE_GOVERNANCE_SECTIONS[id]
      if (!meta) {
        return null
      }
      return { id, ...meta }
    })
    .filter(Boolean)
  return {
    ...entry,
    governanceProfile,
    sections,
  }
}

export function getZipPackageSpec(assetType) {
  return resolveZipPackageSpec(assetType)
}

export function getUploadRequirement(assetType) {
  return summarizeZipPackageSpec(assetType)
}

export function getUploadExample(assetType) {
  const spec = resolveZipPackageSpec(assetType)
  return (spec.examples && spec.examples[0]) || ''
}

export function getUploadHint(assetType) {
  const summary = summarizeZipPackageSpec(assetType)
  if (!summary) {
    return ''
  }
  return `zip 内须含 ${summary}`
}

export function shouldShowZipPackageRequirement(assetType) {
  return hasZipPackageSpec(assetType)
}

/**
 * 是否在「基本信息」下方单独展示类型配置区（与 coupon 的 schema 卡片同级）。
 * 纯文件型 / 仅 zip 提示的类型不再塞进基本信息，避免区块高度乱跳。
 */
export function shouldShowTypeConfigSection(assetType) {
  const entry = resolveGenericResourceTypeEntry(assetType)
  if (!entry || !assetType) {
    return false
  }
  if (entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.SCHEMA) {
    return false
  }
  if (entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.FILE_GENERIC) {
    return false
  }
  if (entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_HINT) {
    return false
  }
  if (entry.mode === GENERIC_RESOURCE_BASIC_FORM_MODE.ZIP_CUSTOM) {
    return false
  }
  return true
}

export function getTypeConfigSectionTitle(assetType) {
  const type = String(assetType || '')
  if (!type) {
    return '资源配置'
  }
  if (type === 'workout_collection') {
    return '合集编排'
  }
  if (type.startsWith('workout_')) {
    return '训练资源配置'
  }
  if (type === 'badge') {
    return '徽章配置'
  }
  if (type === 'stickers') {
    return 'stickers 配置'
  }
  if (type === 'article') {
    return '文章配置'
  }
  if (type.includes('白噪音') || type.includes('sounds')) {
    return '白噪音配置'
  }
  if (type === 'ai_assistant') {
    return 'Ai 角色配置'
  }
  return '资源配置'
}

export function getSchemaSectionTitle(assetType) {
  const type = String(assetType || '')
  if (type === 'coupon(优惠券)') {
    return '优惠券配置'
  }
  if (type === 'web(网页)') {
    return '网页配置'
  }
  return '资源配置'
}
